import Vue from 'utils/vueUtils';
import Profiles from '../collections/Profiles';
import Profile from '../models/Profile';
import TollFreeProfiles from '../collections/TollFreeProfiles';
import TollFreeProfile from '../models/TollFreeProfile';
import { baseApi } from 'core/api';
import { useToastStore } from 'store/modules';

const state = () => ({
    _profileBusiness: new Profile({
        type: 'profile',
        businessProfile: {
            status: 'empty',
        },
        pointsOfContact: [],
    }),
    _profileBusinessLoading: true,
    _profileBusinessStates: null,
    _profileBusinessBusinessTypes: null,
    _profileBusinessRegistrationTypes: null,
    _profileBusinessJobPositions: null,
    _profileBusinessIndustries: null,
    _profileBusinessRegionsOfOperation: null,
    _shakenStirProfile: new Profile({
        type: 'shaken_stir',
        businessProfile: {
            status: 'empty',
        },
    }),
    _CNAMCallerProfile: {
        cnam: [],
    },
    _CNAMCallerFreeNumbers: [],
    _CNAMCallerIdNumbers: [],
    _complianceBillingInfo: {},
    _companyProfile: new Profile({
        type: 'company',
    }),
    _tollFreeProfile: {
        items: [],
    },
    _tollFreeProfileCreateError: null,
    _tollFreeFormFields: {},
    _einFileLoader: {
        size: null,
        name: null,
        isLoading: false,
        progress: 100, // 0 - start loading | 1 - loaded
    },
    _complianceModalsOrder: [],
    _isShowBusinessProfileModal: false,
    _isVerifyEinLoading: false,
    _voiceIntegrity: null,
    _voiceIntegrityLoading: true,
});

const getters = {
    profileBusinessLoading: (state) => state._profileBusinessLoading,
    profileBusiness: (state) => state._profileBusiness,
    profileBusinessEinFileLoader: (state) => state._einFileLoader,
    profileBusinessStates: (state) => state._profileBusinessStates,
    profileBusinessBusinessTypes: (state) => state._profileBusinessBusinessTypes,
    profileBusinessRegistrationTypes: (state) => state._profileBusinessRegistrationTypes,
    profileBusinessJobPositions: (state) => state._profileBusinessJobPositions,
    profileBusinessIndustries: (state) => state._profileBusinessIndustries,
    profileBusinessRegionsOfOperation: (state) => state._profileBusinessRegionsOfOperation,
    shakenStirProfile: (state) => state._shakenStirProfile,
    CNAMCallerProfile: (state) => state._CNAMCallerProfile,
    CNAMCallerFreeNumbers: (state) => state._CNAMCallerFreeNumbers,
    CNAMCallerIdNumbers: (state) => state._CNAMCallerIdNumbers,
    complianceBillingInfo: (state) => state._complianceBillingInfo,
    companyProfile: (state) => state._companyProfile,
    tollFreeProfile: (state) => state._tollFreeProfile,
    tollFreeProfileItems: (state) => state._tollFreeProfile.items,
    tollFreeProfileItemsCompleted: (state) =>
        state._tollFreeProfile.items.filter((item) => item.completed),
    tollFreeFormFields: (state) => state._tollFreeFormFields,
    tollFreeFormFieldsMonthlyValues: (state) => state._tollFreeFormFields.monthly_values,
    tollFreeFormFieldsOptInMethods: (state) => state._tollFreeFormFields.opt_in_methods,
    tollFreeFormFieldsUseCases: (state) => state._tollFreeFormFields.use_cases,
    tollFreeProfileCreateError: (state) => state._tollFreeProfileCreateError,
    complianceModalsOrder: (state) => state._complianceModalsOrder,
    showBusinessProfileModal: (state) => state._isShowBusinessProfileModal,
    isBPVerifyEinLoading: (state) => state._isVerifyEinLoading,
    voiceIntegrity: (state) => state._voiceIntegrity,
    voiceIntegrityLoading: (state) => state._voiceIntegrityLoading,
};

const actions = {
    fetchBusinessProfile() {
        this._setComplienceBusinessProfileLoading(true);

        return baseApi.get('core/organization/business-profile').then((response) => {
            this._setComplienceBusinessProfileLoaded(response.data);
            this._setComplienceBusinessProfileLoading(false);

            return response.data;
        });
    },

    fetchBusinessProfileStates() {
        return baseApi.get('core/organization/business-profile/states').then((response) => {
            this._setComplienceBusinessProfileUpdateState(['profileBusinessStates', response.data]);

            return response.data;
        });
    },

    fetchBusinessProfileBusiness() {
        return baseApi.get('core/organization/business-profile/business-types').then((response) => {
            this._setComplienceBusinessProfileUpdateState([
                'profileBusinessBusinessTypes',
                response.data,
            ]);

            return response.data;
        });
    },

    fetchBusinessProfileRegistration() {
        return baseApi
            .get('core/organization/business-profile/registration-types')
            .then((response) => {
                this._setComplienceBusinessProfileUpdateState([
                    'profileBusinessRegistrationTypes',
                    response.data,
                ]);

                return response.data;
            });
    },

    fetchBusinessProfileJobPositions() {
        return baseApi.get('core/organization/business-profile/job-positions').then((response) => {
            this._setComplienceBusinessProfileUpdateState([
                'profileBusinessJobPositions',
                response.data,
            ]);

            return response.data;
        });
    },

    fetchBusinessProfileIndustry() {
        return baseApi.get('core/organization/business-profile/industries').then((response) => {
            this._setComplienceBusinessProfileUpdateState([
                'profileBusinessIndustries',
                response.data,
            ]);

            return response.data;
        });
    },

    fetchBusinessProfileRegions() {
        return baseApi
            .get('core/organization/business-profile/regions-of-operation')
            .then((response) => {
                this._setComplienceBusinessProfileUpdateState([
                    'profileBusinessRegionsOfOperation',
                    response.data,
                ]);

                return response.data;
            });
    },

    fetchBusinessProfileVerifyEin(businessName) {
        this._setComplienceBusinessProfileUpdateVerifyEinLoading(true);

        return baseApi
            .get(`core/organization/business-profile/verify-ein?q=${businessName}`)
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                return error?.response;
            })
            .finally(() => {
                this._setComplienceBusinessProfileUpdateVerifyEinLoading(false);
            });
    },

    handleUpdateComplianceProfile(data) {
        this._setComplienceBusinessProfileUpdateState(data);
    },

    fetchBusinessProfileGeneralInformation({ replaceField, form, params }) {
        return form
            .post(`core/organization/business-profile/${replaceField}`, params)
            .then((response) => {
                this._setComplienceBusinessProfileUpdate(response.data);

                return response.data;
            });
    },

    deleteBusinessProfileEinDocument() {
        return baseApi
            .delete('core/organization/business-profile/business-information/ein-document')
            .then((response) => {
                return response.data;
            });
    },

    fetchBusinessProfileEinDocument(formData) {
        const fileSize = formData.get('fileSize');
        const fileName = formData.get('fileName');

        const config = {
            onUploadProgress: (progressEvent) => {
                const progress = Math.trunc((progressEvent.loaded / fileSize) * 100);

                this._setComplienceEinFileLoading({
                    isLoading: true,
                    size: fileSize,
                    name: fileName,
                    progress,
                });
            },
        };

        return baseApi
            .post(
                'core/organization/business-profile/business-information/ein-document',
                formData,
                config
            )
            .then((response) => {
                return response.data;
            })
            .finally((e) => {
                this._setComplienceEinFileLoaded();
            });
    },

    fetchShakenStirProfile() {
        return baseApi.get('core/organization/shaken-stir').then((response) => {
            this._setComplienceShakenStirProfile(response.data);
            return response.data;
        });
    },

    deleteShakenStirProfile(id) {
        return baseApi
            .delete('core/organization/shaken-stir', { data: { id } })
            .then((response) => {
                this._setComplienceShakenStirProfileDelete(response.data);

                window.publicApiReact?.Compliance.updateOrganizationShakenStir(response.data);

                return response;
            });
    },

    updateShakenStirProfile(form) {
        return form.post('core/organization/shaken-stir/terms').then((response) => {
            this._setComplienceShakenStirProfileUpdate(response.data);

            window.publicApiReact?.Compliance.updateOrganizationShakenStir(response.data);

            return response.data;
        });
    },

    fetchCNAMCallerProfile() {
        return baseApi.get('core/organization/cnam').then((response) => {
            this._setComplienceCnamCallerProfile(response.data);
            return response.data;
        });
    },

    deleteCNAMCaller(form) {
        return form.deleteWithBody('core/organization/cnam').then((response) => {
            this._setComplienceCnamDelete(form.id);
            window.publicApiReact?.Compliance.deleteCnamItem(form.id);

            return response.data;
        });
    },

    fetchCNAMCallerFreeNumbers() {
        return baseApi.get('core/organization/cnam/free-numbers').then((response) => {
            this._setComplienceCnamFreeNumbers(response.data);

            return response.data;
        });
    },

    fetchCNAMIdNumber(id) {
        return baseApi.get(`core/organization/cnam/${id}/phone-numbers`).then((response) => {
            this._setComplienceCnamIdNumbers(response.data);

            return response.data;
        });
    },

    termsCNAMCaller(form) {
        return form.post('core/organization/cnam/terms').then((response) => {
            window.publicApiReact?.Compliance.updateOrganizationCham(response.data);

            return response.data;
        });
    },

    createCNAMCaller(form) {
        return form.post('core/organization/cnam').then((response) => {
            this._setComplienceCnamCreate(response.data);
            window.publicApiReact?.Compliance.updateOrganizationCham(response.data);

            return response.data;
        });
    },

    validateCNAMCaller(form) {
        return form.post('core/organization/cnam/validate').then((response) => {
            return response.data;
        });
    },

    addCNAMNumbers(params) {
        return baseApi.post('core/organization/cnam/phone-numbers', params).then((response) => {
            this._setComplienceCnamUpdateNumbers({
                id: params.cnam_id,
                numbers: response.data,
            });

            return response.data;
        });
    },

    deleteCNAMNumbers(params) {
        return baseApi
            .delete('core/organization/cnam/phone-numbers', { data: params })
            .then((response) => {
                this._setComplienceCnamUpdateNumbers({
                    id: params.cnam_id,
                    numbers: response.data,
                });

                return response.data;
            });
    },

    deleteComplianceSubscription() {
        return baseApi.post('core/billing/compliance/unsubscribe');
    },

    billingComplianceInfo() {
        return baseApi.get('core/billing/compliance/info').then((response) => {
            this._setComplienceBillingInfo(response.data);

            window.publicApiReact?.Billing.updateBillingComplianceInfo(response.data);

            return response.data;
        });
    },

    billingCompliancePreview(planId) {
        return baseApi.get(`billing/preview/compliance?plan=${planId}`).then((response) => {
            return response.data;
        });
    },

    billingComplianceUpdate() {
        return baseApi.post('core/billing/compliance/subscribe').then((response) => {
            this._setComplienceBillingInfo(response.data);

            window.publicApiReact?.Billing.updateBillingComplianceInfo(response.data);

            return response.data;
        });
    },

    fetchCompanyProfile() {
        return baseApi
            .get('core/organization/company-profile')
            .then((response) => {
                this._setComplienceCompanyProfile(response.data.data);

                return response.data.data;
            })
            .catch((e) => {
                this._setComplienceCompanyProfile(null);
            });
    },

    updateCompanyProfile(form) {
        return form.post('core/organization/company-profile').then((response) => {
            this._setComplienceCompanyProfile(response.data.data);

            return response.data.data;
        });
    },

    fetchTollFreeProfile() {
        return baseApi.get('core/organization/toll-free').then((response) => {
            const items = response.data.data;

            window.publicApiReact?.Compliance.addTollFreeProfiles(items);

            this._setComplienceTollFreeProfile(items);

            return response.data.data;
        });
    },

    fetchTollFreeFormFields() {
        return baseApi.get('core/organization/toll-free/form-fields').then((response) => {
            this._setComplienceTollFreeFormFields(response.data);

            return response.data;
        });
    },

    getTollFreeVerify(id) {
        return baseApi.get(`core/organization/toll-free/verify/${id}`).then((response) => {
            return response.data.data;
        });
    },

    getTollFreePhoneVerify({ profile_id, number_id }) {
        const data = { profile_id };

        if (number_id) {
            data.number_id = number_id;
        }

        return baseApi
            .post('core/organization/toll-free/resubmit-verification', data)
            .then((response) => {
                return response.data.data;
            });
    },

    createTollFreeProfile(form) {
        return form
            .post('core/organization/toll-free')
            .then((response) => {
                const item = response.data.data;

                window.publicApiReact?.Compliance.addTollFreeProfile(item);

                this._setComplienceTollFreeProfileCreate(item);

                return response.data.data;
            })
            .catch((err) => {
                this._setComplienceTollFreeProfileCreateError(err.response.data);

                for (let errors in err.response.data) {
                    for (let error in err.response.data[errors]) {
                        useToastStore().showToast({
                            type: 'danger',
                            message: err.response.data[errors][error],
                        });
                    }
                }
            });
    },

    updateTollFreeProfile({ form, id }) {
        return form.put(`core/organization/toll-free/${id}`).then((response) => {
            const item = response.data.data;

            window.publicApiReact?.Compliance.addTollFreeProfile(item);
            this._setComplienceTollFreeProfileUpdate(item);

            this.fetchTollFreeProfile();

            return item;
        });
    },

    deleteTollFreeProfile({ form, id }) {
        return form.delete(`core/organization/toll-free/${id}`).then((response) => {
            window.publicApiReact?.Compliance.deleteTollFreeProfile(id);

            this._setComplienceTollFreeProfileDelete(id);

            return response.data;
        });
    },

    updateComplianceModalsOrder(order) {
        this._setComplienceUpdateModalsOrder(order);
    },

    updateBusinessProfileModalState(status) {
        this._setComplienceUpdateBusinessProfileModalsState(status);
    },

    updateBusinessProfileTollFreeAIInformation({ form, params }) {
        return form
            .post('core/organization/toll-free/business-information/save', params)
            .then((response) => {
                this.fetchBusinessProfile();

                return response.data;
            });
    },

    fetchVoiceIntegrity() {
        this._setComplianceVoiceIntegrityLoading();

        return baseApi.get('/core/ten-dlc/voice-integrity').then((response) => {
            this._setComplianceVoiceIntegrityLoaded(response.data);
            window.publicApiReact?.Compliance.updateOrganizationVoiceIntegrity(response.data);
        });
    },

    setVoiceIntegrity(form) {
        return form.post('/core/ten-dlc/voice-integrity').then((response) => {
            window.publicApiReact?.Compliance.updateOrganizationVoiceIntegrity(response.data);
        });
    },

    _setComplienceBusinessProfileLoading(status = false) {
        this._profileBusinessLoading = status;
    },

    _setComplienceBusinessProfileLoaded(data) {
        this._profileBusiness = new Profile({
            type: 'profile',
            ...data,
        });
    },

    _setComplienceBusinessProfileUpdate(data) {
        this._profileBusiness = new Profile({
            type: 'profile',
            ...data,
        });
    },

    _setComplienceBusinessProfileUpdateState([key, value]) {
        this[`_${key}`] = value;
    },

    _setComplienceShakenStirProfile(profile) {
        const shakenStir = profile.shakenStir || {};

        this._shakenStirProfile = new Profile({
            type: 'shaken_stir',
            businessProfile: {
                status: 'empty',
                terms: profile.terms,
                ...shakenStir,
            },
        });
    },

    _setComplienceShakenStirProfileUpdate(profile) {
        const shakenStir = profile.shakenStir || {};
        this._shakenStirProfile.businessProfile = {
            ...this._shakenStirProfile.businessProfile,
            ...shakenStir,
        };
    },

    _setComplienceShakenStirProfileDelete(profile) {
        const shakenStir = profile.shakenStir || {};

        this._shakenStirProfile = new Profile({
            type: 'shaken_stir',
            businessProfile: {
                status: 'empty',
                terms: profile.terms,
                ...shakenStir,
            },
        });
    },

    _setComplienceCnamCallerProfile(profile) {
        const cnam = (profile.cnam || []).map((item) => ({
            type: 'cnam',
            businessProfile: {
                status: 'empty',
                ...item,
            },
        }));

        this._CNAMCallerProfile = {
            cnam: new Profiles(cnam).models,
        };
    },

    _setComplienceCnamFreeNumbers(numbers) {
        this._CNAMCallerFreeNumbers = numbers.map(({ id, number }) => {
            return {
                id: id,
                value: number,
                label: number,
            };
        });
    },

    _setComplienceCnamIdNumbers(numbers) {
        if (numbers.error) {
            numbers = [];
        }

        this._CNAMCallerIdNumbers = numbers.map((item) => ({
            ...item,
            label: item.number,
        }));
    },

    _setComplienceCnamUpdateNumbers({ id, numbers }) {
        if (numbers.error) {
            numbers = [];
        }

        this._CNAMCallerProfile.cnam = this._CNAMCallerProfile.cnam.map((item) => {
            if (item.businessProfile.id === id) {
                item.businessProfile.count = numbers.length;
            }

            return item;
        });
    },

    _setComplienceCnamCreate(profile) {
        const cnam = (profile.cnam || []).map((item) => ({
            type: 'cnam',
            businessProfile: {
                status: 'empty',
                ...item,
            },
        }));

        this._CNAMCallerProfile.cnam = new Profiles(cnam).models;
    },

    _setComplienceCnamDelete(id) {
        this._CNAMCallerProfile.cnam = this._CNAMCallerProfile.cnam.filter(
            (item) => item.businessProfile.id !== id
        );
    },

    _setComplienceBillingInfo(data) {
        this._complianceBillingInfo = data;
    },

    _setComplienceEinFileLoaded() {
        this._einFileLoader = {
            size: null,
            name: null,
            isLoading: false,
            progress: 100,
        };
    },

    _setComplienceEinFileLoading(data) {
        this._einFileLoader = data;
    },

    _setComplienceCompanyProfile(data) {
        let profile = {};
        if (data) {
            profile = {
                businessProfile: {
                    status: 'approved',
                },
                businessInformation: {
                    first_name: data.first_name,
                    last_name: data.last_name,
                    phone_number: data.phone_number,
                    registration_number: data.registration_number,
                    business_name: data.business_name,
                    city: data.city,
                    country_id: data.country_id,
                    state_id: data.state_id,
                    street_address_1: data.street_address_1,
                    street_address_2: data.street_address_2,
                    website_url: data.website_url,
                    zip_code: data.zip_code,
                },
            };
        }

        this._companyProfile = new Profile({
            type: 'company',
            businessProfile: {
                status: 'empty',
            },
            businessInformation: {},
            ...profile,
        });
    },

    _setComplienceTollFreeProfile(data) {
        Vue.set(this._tollFreeProfile, 'items', new TollFreeProfiles(data).models);
    },

    _setComplienceTollFreeFormFields(data) {
        this._tollFreeFormFields = data;
    },

    _setComplienceTollFreeProfileCreate(data) {
        this._tollFreeProfile.items.push(new TollFreeProfile(data));
    },

    _setComplienceTollFreeProfileCreateError(data) {
        this._tollFreeProfileCreateError = data;
    },

    _setComplienceTollFreeProfileUpdate(data) {
        this._tollFreeProfile.items = this._tollFreeProfile.items.map((item) => {
            if (data.id === item.id) {
                return new TollFreeProfile(data);
            }

            return item;
        });
    },

    _setComplienceTollFreeProfileDelete(id) {
        this._tollFreeProfile.items = this._tollFreeProfile.items.filter((item) => item.id !== id);
    },

    _setComplienceUpdateModalsOrder(order) {
        this._complianceModalsOrder = order;
    },

    _setComplienceUpdateBusinessProfileModalsState(status) {
        this._isShowBusinessProfileModal = status;
    },

    _setComplienceBusinessProfileUpdateVerifyEinLoading(status) {
        this._isVerifyEinLoading = status;
    },

    _setComplianceVoiceIntegrityLoading() {
        this._voiceIntegrityLoading = true;
    },

    _setComplianceVoiceIntegrityLoaded(data) {
        this._voiceIntegrityLoading = false;
        this._voiceIntegrity = data;
    },
};

export default {
    state,
    getters,
    actions,
};
